import React, { useState, useEffect, forwardRef } from 'react';
import FormField from '../ui/FormField';
import FormHeader from '../personal-info/FormHeader';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Eye, EyeOff, AlertCircle, Lock } from '../common/IconsComponent';
import { useUser } from '../../contexts/UserContext';
import SpinnerOverlay from '../ui/SpinnerOverlay';

// ─── Schema ───────────────────────────────────────────────────────────────────

const zoomSchema = Yup.object().shape({
  zoomAccountId: Yup.string().required('נדרש מזהה חשבון Zoom'),
  zoomClientId:  Yup.string().required('נדרש מזהה לקוח Zoom'),
  zoomSecretKey: Yup.string().required('נדרש מפתח סודי Zoom'),
});

// ─── Component ────────────────────────────────────────────────────────────────

const ZoomSection = forwardRef(({ active }, ref) => {
  const { user, updateUserProfile, showAlert } = useUser();
  const [showSecret, setShowSecret] = useState(false);
  const [isLoading,  setIsLoading ] = useState(false);

  const formik = useFormik({
    initialValues: { zoomAccountId: '', zoomClientId: '', zoomSecretKey: '' },
    validationSchema: zoomSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await updateUserProfile(values);
        showAlert('הפרטים נשמרו בהצלחה', 'success');
        formik.setStatus(null);
      } catch {
        formik.setStatus('אירעה שגיאה בשמירת הפרטים');
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (user) formik.setValues({
      zoomAccountId: user.zoomAccountId || '',
      zoomClientId:  user.zoomClientId  || '',
      zoomSecretKey: user.zoomSecretKey || '',
    });
  }, [user]);

  if (!active) return null;

  return (
    <div dir="rtl" ref={ref} id="zoom-section" className="scroll-mt-24">
      <div className="bg-white rounded-2xl shadow-md border border-indigo-50 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">

        {isLoading && (
          <SpinnerOverlay
            variant="glow"
            text="נא להמתין"
            dark={false}
            blur={false}
          />
        )}

        <form onSubmit={formik.handleSubmit} noValidate>
          <FormHeader
            title="פרטי חשבון Zoom"
            buttonText="שמור פרטים"
            disabled={isLoading}
          />

          {/* Server error */}
          {formik.status && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 text-red-700 rounded-xl text-sm border border-red-100">
              <AlertCircle size={15} className="flex-shrink-0" />
              <span>{formik.status}</span>
            </div>
          )}

          <div className="space-y-4 sm:space-y-5">

            {/* Account ID */}
            <FormField
              label="מזהה חשבון Zoom"
              name="zoomAccountId"
              type="text"
              value={formik.values.zoomAccountId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Account ID"
              icon={<Lock size={15} className="text-indigo-500" />}
              required
              error={formik.touched.zoomAccountId && formik.errors.zoomAccountId}
            />

            {/* Client ID */}
            <FormField
              label="מזהה לקוח Zoom"
              name="zoomClientId"
              type="text"
              value={formik.values.zoomClientId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Client ID"
              icon={<Lock size={15} className="text-indigo-500" />}
              required
              error={formik.touched.zoomClientId && formik.errors.zoomClientId}
            />

            {/* Secret key */}
            <div className="relative">
              <FormField
                label="מפתח סודי Zoom"
                name="zoomSecretKey"
                type={showSecret ? 'text' : 'password'}
                value={formik.values.zoomSecretKey}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Client Secret"
                icon={<Lock size={15} className="text-indigo-500" />}
                required
                error={formik.touched.zoomSecretKey && formik.errors.zoomSecretKey}
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                className="absolute left-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showSecret ? 'הסתר מפתח' : 'הצג מפתח'}
              >
                {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Security note */}
          <div className="flex items-center gap-2 sm:gap-3 mt-5 sm:mt-6 p-3 sm:p-4 bg-indigo-50 rounded-xl border border-indigo-100">
            <Lock className="text-indigo-500 flex-shrink-0" size={18} />
            <div>
              <p className="text-sm font-medium text-gray-800">אבטחת פרטים</p>
              <p className="text-xs text-gray-500 mt-0.5">הפרטים מוצפנים ונשמרים באופן מאובטח</p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default ZoomSection;